import { useEffect, useState } from 'react';
import {
  UMButton, UMFormGroup, UMFormStyles, UMIcon, UMInput, UMR, UMSegmentedControl, UMSelect,
} from '../../urbanmap/_components';
import { PLAN } from './tokens';
import type { PlanBootstrap } from './types';
import { Modal, fmt } from './ui';

function Column({ icon, title, subtitle, tone, items }: {
  icon: string; title: string; subtitle: string; tone: 'copy' | 'live'; items: { label: string; value?: string }[];
}) {
  const accent = tone === 'copy' ? PLAN.accent : UMR.blue;
  return (
    <section style={{ flex: 1, border: `1px solid ${UMR.border}`, borderTop: `4px solid ${accent}`, padding: 16, display: 'flex', flexDirection: 'column', gap: 8 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <UMIcon name={icon} size={22} color={accent} />
        <div style={{ fontWeight: 700, fontSize: 16 }}>{title}</div>
      </div>
      <div style={{ fontSize: 12, color: UMR.textSecondary }}>{subtitle}</div>
      <ul style={{ listStyle: 'none', margin: 0, padding: 0, display: 'flex', flexDirection: 'column', gap: 6 }}>
        {items.map((it) => (
          <li key={it.label} style={{ display: 'flex', justifyContent: 'space-between', gap: 8, fontSize: 14, borderBottom: `1px solid ${UMR.borderSubtle}`, paddingBottom: 4 }}>
            <span>{it.label}</span>
            {it.value && <strong>{it.value}</strong>}
          </li>
        ))}
      </ul>
    </section>
  );
}

export function CreateProjectDialog({ data, onCancel, onCreated }: { data: PlanBootstrap; onCancel: () => void; onCreated: (name: string) => void }) {
  const [name, setName] = useState(data.project.name);
  const [perimeter, setPerimeter] = useState('selection');
  const [kind, setKind] = useState('Réseau existant');
  const [progress, setProgress] = useState<number | null>(null);
  const inside = data.geo.buildings.features.filter((f) => f.properties?.inside === 1).length;

  useEffect(() => {
    if (progress === null) return;
    if (progress >= 100) {
      const t = window.setTimeout(() => onCreated(name), 300);
      return () => window.clearTimeout(t);
    }
    const t = window.setTimeout(() => setProgress((p) => Math.min(100, (p ?? 0) + 20)), 220);
    return () => window.clearTimeout(t);
  }, [progress, name, onCreated]);

  const steps = ['Copie des bâtiments', 'Copie de la voirie', 'Copie des réseaux', 'Création du projet'];
  const copying = progress !== null;

  return (
    <Modal
      title="Nouveau projet Plan"
      onClose={copying ? () => undefined : onCancel}
      width={820}
      footer={
        copying ? undefined : (
          <>
            <UMButton variant="secondary" onClick={onCancel}>Annuler</UMButton>
            <UMButton icon="content_copy" onClick={() => setProgress(0)} disabled={!name.trim()}>Créer la copie de travail</UMButton>
          </>
        )
      }
    >
      <UMFormStyles />
      {copying ? (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16, padding: '24px 0' }}>
          <div style={{ fontSize: 16, fontWeight: 600 }}>Copie du périmètre depuis UrbanMap…</div>
          <div style={{ height: 6, background: UMR.grey100 }}>
            <div style={{ height: '100%', width: `${progress}%`, background: PLAN.accent, transition: 'width 200ms' }} />
          </div>
          <ul style={{ listStyle: 'none', margin: 0, padding: 0, display: 'flex', flexDirection: 'column', gap: 6 }}>
            {steps.map((s, i) => {
              const done = (progress ?? 0) >= (i + 1) * 25;
              return (
                <li key={s} style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 14, color: done ? UMR.black : UMR.textSecondary }}>
                  <UMIcon name={done ? 'check_circle' : 'radio_button_unchecked'} size={18} color={done ? PLAN.accent : UMR.textSecondary} />
                  {s}
                </li>
              );
            })}
          </ul>
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
          <div style={{ display: 'grid', gridTemplateColumns: '2fr 1.4fr 1fr', gap: 16 }}>
            <UMFormGroup label="Nom du projet">
              <UMInput value={name} onChange={setName} ariaLabel="Nom du projet" />
            </UMFormGroup>
            <UMFormGroup label="Périmètre">
              <UMSelect
                value={perimeter}
                onChange={setPerimeter}
                ariaLabel="Périmètre"
                options={[
                  { value: 'selection', label: `Sélection UM : ${data.project.perimeterLabel}` },
                  { value: 'commune', label: 'Communes : Gentilly + Le Kremlin-Bicêtre' },
                  { value: 'epci', label: 'EPT Grand-Orly Seine Bièvre' },
                  { value: 'draw', label: 'Dessiner un contour…' },
                ]}
              />
            </UMFormGroup>
            <UMFormGroup label="Type de projet">
              <UMSegmentedControl options={['Réseau existant', 'Réseau neuf']} value={kind} onChange={setKind} />
            </UMFormGroup>
          </div>

          <div style={{ display: 'flex', gap: 16 }}>
            <Column
              tone="copy"
              icon="content_copy"
              title="Copié dans le projet"
              subtitle="Figé à la date de création · entièrement modifiable · partagé avec le projet"
              items={[
                { label: 'Bâtiments du périmètre', value: fmt(inside) },
                { label: 'Voirie', value: '18,4 km' },
                { label: kind === 'Réseau neuf' ? 'Réseaux existants (contexte)' : 'Réseaux de chaleur existants', value: '2 · 3,1 km' },
                { label: 'IDs fonctionnels UrbanMap', value: 'conservés' },
              ]}
            />
            <Column
              tone="live"
              icon="sync"
              title="Reste en direct depuis UrbanMap"
              subtitle="Lecture seule · non limité au périmètre · toujours à jour"
              items={[
                { label: 'Zones de développement prioritaire' },
                { label: 'Cadastre & IRIS' },
                { label: 'Potentiels EnR&R (géothermie, chaleur fatale)' },
                { label: 'Fonds de carte' },
              ]}
            />
          </div>

          <div style={{ display: 'flex', gap: 12, padding: 16, background: PLAN.warningBg, border: `1px solid ${PLAN.warningBorder}`, color: PLAN.warningText, fontSize: 14 }}>
            <UMIcon name="sync_disabled" size={22} color={PLAN.warningText} />
            <div>
              <strong>Pas de synchronisation avec UrbanMap.</strong> Les modifications faites ensuite dans UrbanMap ne seront pas
              reportées dans ce projet, et inversement. Plan vous signalera simplement les bâtiments qui ont évolué dans UrbanMap
              depuis la copie.
            </div>
          </div>
        </div>
      )}
    </Modal>
  );
}
